import React, { useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Input } from "@/components/ui/input";
import { ArrowUpDown } from "lucide-react";
import { gbp } from "@/components/analytics/AnalyticsParts";
import { Delta } from "@/components/ga/GaSections";

const num = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-GB"));
const pct = (v) => (v == null ? "–" : `${(v * 100).toFixed(1)}%`);
const pos = (v) => (v == null ? "–" : Number(v).toFixed(1));
const chg = (v) => (v == null ? "–" : <span className={v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-400"}>{v > 0 ? "+" : ""}{Math.round(v * 10) / 10}</span>);
export const F = { clicks: ["Clicks", num], impressions: ["Impr.", num], ctr: ["CTR", pct], position: ["Position", pos], clicks_change: ["Δ clicks", chg],
  impressions_change: ["Δ impr.", chg], position_change: ["Δ pos. (+ better)", chg], prev_clicks: ["Prev clicks", num], prev_impressions: ["Prev impr.", num],
  expected_ctr: ["Expected CTR", pct], missed_clicks: ["Missed clicks", num], ga4_sessions: ["GA4 sessions", num], ga4_engagement_rate: ["GA4 eng.", pct],
  ga4_enquiries: ["GA4 enquiries", num], wc_organic_orders: ["WC orders", num], wc_organic_revenue: ["WC revenue", gbp] };

export function SortTable({ rows, label, labelTitle, mets, testid, initial = "clicks", flag }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState([initial, -1]);
  const data = useMemo(() => (rows || []).filter((r) => !q || String(r[label]).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => ((a[sort[0]] ?? -1e9) - (b[sort[0]] ?? -1e9)) * sort[1]).slice(0, 100), [rows, q, sort, label]);
  return (
    <div data-testid={testid}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Filter ${labelTitle.toLowerCase()}…`} className="h-8 w-full sm:w-64 mb-2" data-testid={`${testid}-filter`} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left"><th className="py-2 pr-3 font-semibold">{labelTitle}</th>
            {mets.map((m) => <th key={m} className="py-2 px-2 font-semibold text-right"><button onClick={() => setSort([m, sort[0] === m ? -sort[1] : -1])} className="inline-flex items-center gap-1 hover:text-slate-700" data-testid={`${testid}-sort-${m}`}>{F[m][0]}<ArrowUpDown className="w-3 h-3" /></button></th>)}</tr></thead>
          <tbody>{data.map((r, i) => (
            <tr key={i} className={`border-t border-slate-100 ${flag && flag(r) ? "bg-amber-50/60" : ""}`}>
              <td className="py-1.5 pr-3 text-slate-700 max-w-[320px] truncate" title={r[label]}>{r[label]}{r.branded ? <span className="ml-1 text-[10px] text-indigo-600 font-semibold">BRAND</span> : null}</td>
              {mets.map((m) => <td key={m} className="py-1.5 px-2 text-right">{F[m][1](r[m])}</td>)}
            </tr>))}
            {!data.length && <tr><td colSpan={mets.length + 1} className="py-3 text-slate-400">No rows.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Overview({ r }) {
  const t = r.totals, p = r.prev_totals;
  const cards = [["Search Console clicks", num(t.clicks), t.clicks, p.clicks], ["Impressions", num(t.impressions), t.impressions, p.impressions],
    ["Average CTR", pct(t.ctr), t.ctr, p.ctr], ["Average position", pos(t.position), t.position, p.position, true],
    ["Branded clicks", num(r.branded.branded.clicks), r.branded.branded.clicks, r.branded.branded.prev_clicks], ["Non-branded clicks", num(r.branded.non_branded.clicks), r.branded.non_branded.clicks, r.branded.non_branded.prev_clicks]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="gsc-overview">
      {cards.map(([l, v, c, pr, inv]) => (
        <div key={l} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid={`gsc-kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{l}</div><div className="text-xl font-bold text-slate-900 mt-1">{v}</div><Delta cur={c} prev={pr} invert={inv} />
        </div>))}
    </div>
  );
}

export function Trend({ daily }) {
  return (
    <div className="h-72" data-testid="gsc-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={daily} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} minTickGap={16} />
          <YAxis yAxisId="i" tick={{ fontSize: 11 }} /><YAxis yAxisId="c" orientation="right" tick={{ fontSize: 11 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="i" dataKey="impressions" name="Impressions" fill="#e0e7ff" /><Line yAxisId="c" dataKey="clicks" name="Clicks" stroke="#4f46e5" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export const Categories = ({ rows }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="gsc-categories">
    {rows.map((c) => (
      <div key={c.category} className="rounded-xl border border-slate-200 p-3" data-testid={`gsc-cat-${c.category}`}>
        <div className="text-xs font-semibold text-slate-600">{c.category}</div><div className="text-lg font-bold">{num(c.impressions)} <span className="text-xs font-normal text-slate-400">impr.</span></div>
        <div className="text-xs flex gap-2">{num(c.clicks)} clicks <Delta cur={c.impressions} prev={c.prev_impressions} /></div>
      </div>))}
  </div>
);

const LV = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-600" };
export const Finding = ({ f, i }) => (
  <li className="rounded-xl border border-slate-200 p-4 space-y-1" data-testid={`gsc-finding-${i}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${f.kind === "problem" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{f.kind === "problem" ? "Problem" : "Opportunity"}</span>
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${f.confidence === "confirmed" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600 italic"}`} data-testid={`gsc-finding-confidence-${i}`}>{f.confidence === "confirmed" ? "Confirmed" : "Hypothesis"}</span>
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${LV[f.priority] || LV.low}`}>{f.priority}</span>
      <b className="text-sm text-slate-900">{f.title}</b>
    </div>
    <p className="text-sm text-slate-700"><b>Evidence:</b> {f.evidence}</p>
    <p className="text-sm text-slate-600"><b>Likely cause:</b> {f.likely_cause}</p>
    <p className="text-sm text-slate-600"><b>Commercial significance:</b> {f.commercial_significance}</p>
    <p className="text-sm text-slate-800"><b>Suggested action:</b> {f.action}</p>
    {f.source && <p className="text-[11px] text-slate-400">Sources: {f.source}</p>}
  </li>
);
